/**
 * How full each Fabric cluster is: namespaces Studio placed there against the registry's soft
 * capacity, the orgs and apps on it, and what the cluster's own platform API counts. Rules and how
 * to add a cluster: docs/studio/fabric-namespaces.md.
 */
import { useEffect, useState } from 'react';

import { adminClient, type AdminFabricCapacity, type AdminFabricCapacityLevel, type AdminFabricCapacityRow } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import type { StatusTone } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

const COLUMNS = [
  { label: 'Cluster' },
  { label: 'Namespaces' },
  { label: 'Organizations' },
  { label: 'Apps' },
  { label: 'Dedicated to' },
  { label: 'Cluster reports' },
];

const LEVEL_TONES: Record<AdminFabricCapacityLevel, StatusTone> = { ok: 'success', warning: 'warning', full: 'danger' };
const LEVEL_LABELS: Record<AdminFabricCapacityLevel, string> = { ok: 'Room', warning: 'Filling up', full: 'Full' };
const BAR_CLASSES: Record<AdminFabricCapacityLevel, string> = { ok: 'bg-success', warning: 'bg-warning', full: 'bg-destructive' };
const TEXT_CLASSES: Record<AdminFabricCapacityLevel, string> = {
  ok: 'text-foreground',
  warning: 'text-warning-foreground',
  full: 'text-destructive-foreground',
};

function count(value: number): string {
  return value.toLocaleString('en-US');
}

function Drift({ row }: { row: AdminFabricCapacityRow }) {
  if (row.platform.state !== 'ok') {
    return (
      <>
        <StatusPill tone="neutral">Not answering</StatusPill>
        <p className="mt-1 max-w-[16rem] truncate text-xs text-muted-foreground" title={row.platform.reason}>
          {row.platform.reason}
        </p>
      </>
    );
  }
  const drift = row.drift ?? 0;
  return (
    <>
      <span className="text-sm">{count(row.platform.namespaces)}</span>
      {drift === 0 ? (
        <p className="text-xs text-muted-foreground">Matches Studio</p>
      ) : (
        <p className="text-xs text-warning-foreground">
          {drift > 0 ? `${count(drift)} more than Studio placed` : `${count(-drift)} fewer than Studio placed`}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Read {formatDateTime(row.platform.readAt)}</p>
    </>
  );
}

export function FabricCapacityTable({ items }: { items: AdminFabricCapacityRow[] }) {
  return (
    <tbody>
      {items.map((row) => (
        <DataTableRow key={row.fabricId}>
          <DataTableCell>
            <span className="font-medium text-foreground">{row.displayName}</span>
            <p className="font-mono text-xs text-muted-foreground">{row.fabricId}</p>
            <div className="mt-1 space-x-1">
              <StatusPill tone={LEVEL_TONES[row.level]}>{LEVEL_LABELS[row.level]}</StatusPill>
              {row.default ? <StatusPill tone="neutral">Default</StatusPill> : null}
              {row.acceptingOrgs ? null : <StatusPill tone="neutral">Closed</StatusPill>}
            </div>
          </DataTableCell>
          <DataTableCell className="min-w-[12rem]">
            <span className={cn('text-sm font-medium', TEXT_CLASSES[row.level])}>
              {count(row.used.namespaces)} / {count(row.namespaceCapacity)}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">{row.usedPercent}%</span>
            <div
              className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label={`${row.displayName} namespaces used`}
              aria-valuemin={0}
              aria-valuemax={row.namespaceCapacity}
              aria-valuenow={row.used.namespaces}
            >
              <div className={cn('h-full rounded-full', BAR_CLASSES[row.level])} style={{ width: `${Math.min(100, row.usedPercent)}%` }} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Warns at {count(row.warningAt)} ({row.capacityWarningPercent}%)
            </p>
          </DataTableCell>
          <DataTableCell>
            <span className="text-sm">{count(row.used.organizations)}</span>
          </DataTableCell>
          <DataTableCell>
            <span className="text-sm">{count(row.used.apps)}</span>
          </DataTableCell>
          <DataTableCell>
            {row.dedicatedOrgIds.length ? (
              row.dedicatedOrgIds.map((orgId) => (
                <p key={orgId} className="font-mono text-xs">
                  {orgId}
                </p>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">Shared</span>
            )}
          </DataTableCell>
          <DataTableCell>
            <Drift row={row} />
          </DataTableCell>
        </DataTableRow>
      ))}
    </tbody>
  );
}

export function FabricCapacitySection() {
  const [capacity, setCapacity] = useState<AdminFabricCapacity | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    adminClient
      .getFabricCapacity()
      .then((result) => {
        if (!cancelled) setCapacity(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load Fabric capacity');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const items = capacity?.items ?? [];

  return (
    <AdminSection
      title="Fabric capacity"
      description={
        <>
          Namespaces Studio has placed on each cluster, against the soft limit in the registry. A full cluster takes no new
          namespaces; new ones go to the next cluster with room, and when none has room publishing fails until a cluster is
          added. Cluster reports is what the cluster itself lists, Studio&apos;s or not.
          {capacity ? ` Last refresh ${formatDateTime(capacity.refreshedAt)}.` : ''}
        </>
      }
      actions={
        <Button type="button" variant="outline" size="sm" onClick={() => setReloadKey((key) => key + 1)} disabled={loading}>
          Refresh
        </Button>
      }
      bodyClassName="p-0"
    >
      <DataTable>
        <DataTableHead columns={COLUMNS} />
        {loading && !capacity ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading Fabric capacity
          </DataTableMessage>
        ) : error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : items.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>No Fabric clusters are registered for this environment</DataTableMessage>
        ) : (
          <FabricCapacityTable items={items} />
        )}
      </DataTable>
    </AdminSection>
  );
}
